import { useState, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import {
  PublicClientApplication,
  type Configuration,
  InteractionRequiredAuthError,
  BrowserAuthError,
  BrowserAuthErrorCodes,
} from '@azure/msal-browser';
import { MsalProvider, useMsal, useIsAuthenticated, useAccount } from '@azure/msal-react';
import { AuthContext } from './context';
import type { AuthContextValue } from './types';

const LOGIN_SCOPES = ['openid', 'profile', 'email'];

const MSG_NO_ACCESS =
  'This account does not have access to the application. Contact your administrator.';
const MSG_CONNECTION =
  'Could not reach the authentication service. Check your connection.';
const MSG_FALLBACK =
  'Sign in failed. Try again or contact your administrator.';
const MSG_CANCELLED =
  'Sign in was cancelled. Make sure you are using an account with access to this application.';

// AADSTS codes (and generic tokens) that map to MSG_NO_ACCESS.
// Checked as substrings against the lowercased MSAL error message.
const NO_ACCESS_FINGERPRINTS = [
  'access_denied',
  'aadsts50011',  // redirect URI not registered in the app
  'aadsts50020',  // account not in tenant
  'aadsts50105',  // user not assigned to an app role
  'aadsts65004',  // user did not consent
  'aadsts70011',  // invalid scope
  'aadsts90072',  // personal Microsoft account not allowed
  'aadsts700016', // application not found in tenant
];

/**
 * Maps an MSAL error to a user-readable string.
 * AADSTS codes are matched as substrings of the lowercased error message
 * because MSAL wraps them in a longer description rather than exposing them
 * as a typed field.
 */
function msalErrorMessage(err: unknown): string {
  if (err instanceof BrowserAuthError) {
    if (err.errorCode === BrowserAuthErrorCodes.popupWindowError) return MSG_CONNECTION;
    // userCancelled covers both genuine cancellations and the case where Entra
    // shows its own error inside the popup and the user closes it manually.
    if (err.errorCode === BrowserAuthErrorCodes.userCancelled) return MSG_CANCELLED;
    // timed_out / monitor_window_timeout: the popup stayed open past the 60s
    // MSAL deadline without completing the redirect — typically caused by a
    // redirect URI mismatch leaving the popup stuck on an Entra error page.
    if (err.errorCode === 'timed_out' || err.errorCode === 'monitor_window_timeout') {
      return MSG_CANCELLED;
    }
    // block_nested_popups: the popup loaded the full app and tried to open
    // another popup. Prevented by the window.opener guard in main.tsx.
    if (err.errorCode === 'block_nested_popups') {
      return MSG_CANCELLED;
    }
    // post_request_failed: the HTTP POST to the token endpoint failed —
    // network issue or a redirect_uri mismatch (trailing-slash difference
    // between window.location.origin and the URI registered in Entra).
    if (err.errorCode === 'post_request_failed') {
      return MSG_CONNECTION;
    }
  }
  if (err instanceof Error) {
    const lower = err.message.toLowerCase();
    if (NO_ACCESS_FINGERPRINTS.some((fp) => lower.includes(fp))) return MSG_NO_ACCESS;
    if (lower.includes('network') || lower.includes('fetch')) return MSG_CONNECTION;
  }
  return MSG_FALLBACK;
}

interface MsalContextBridgeProps {
  apiScope: string;
  children: ReactNode;
}

// MsalContextBridge adapts MSAL's React context to our app's AuthContext shape.
function MsalContextBridge({ apiScope, children }: MsalContextBridgeProps) {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Resolve the loading state once MSAL finishes its internal cache check.
  useEffect(() => {
    instance.initialize().finally(() => setIsLoading(false));
  }, [instance]);

  /**
   * Triggers a login popup. Falls back to a redirect if the popup is blocked
   * by the browser. Sets the returned account as the active MSAL account so
   * subsequent silent token calls resolve without an account-resolution step.
   */
  const signIn = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await instance.loginPopup({ scopes: LOGIN_SCOPES, prompt: 'select_account' });
      if (result.account) instance.setActiveAccount(result.account);
    } catch (err) {
      if (
        err instanceof BrowserAuthError &&
        err.errorCode === BrowserAuthErrorCodes.popupWindowError
      ) {
        try {
          await instance.loginRedirect({ scopes: LOGIN_SCOPES, prompt: 'select_account' });
          return;
        } catch {
          // redirect also failed — fall through to show error
        }
      }
      console.error('[auth] signIn error:', err);
      const msg = msalErrorMessage(err);
      if (msg) setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [instance]);

  /**
   * Clears the MSAL token cache and reloads the app to the origin.
   * Uses `clearCache` + `location.replace` instead of `logoutPopup` to avoid
   * an extra Entra network round-trip. Pass `errorMsg` to surface it on the
   * login page after the reload (stored before the navigate).
   */
  const signOut = useCallback(async (errorMsg?: string) => {
    if (errorMsg) setError(errorMsg);
    await instance.clearCache();
    window.location.replace(window.location.origin);
  }, [instance]);

  /**
   * Returns a valid access token for the API scope, trying silent acquisition
   * first. Falls back to a popup on `InteractionRequiredAuthError` (e.g.
   * expired session, MFA step-up). The scope must be the API scope exposed in
   * the Entra app registration (e.g. `api://<id>/access_as_user`) so the
   * returned token carries the correct `aud` claim. Throws if no access token
   * is returned — the caller must not fall back to the id token.
   *
   * Pass `forceRefresh: true` to bypass MSAL's access-token cache and mint a
   * brand-new token. Clients set this when a request was rejected mid-session
   * (401/403) so the retry does not re-send the same token MSAL still believes
   * is valid — the case that broke reactive refresh before.
   *
   * Pass `options.silentOnly: true` to suppress the popup fallback: on
   * InteractionRequiredAuthError the call rejects instead of opening a popup.
   * For opportunistic refreshes that aren't a reaction to an already-broken
   * request, a popup would be a surprising side effect of an otherwise
   * successful, unrelated action.
   */
  const getAccessToken = useCallback(async (
    forceRefresh = false,
    options?: { silentOnly?: boolean }
  ): Promise<string> => {
    const active = account ?? instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (!active) throw new Error('No active account');
    try {
      const result = await instance.acquireTokenSilent({
        scopes: [apiScope],
        account: active,
        forceRefresh,
      });
      if (!result.accessToken) throw new Error('acquireTokenSilent returned no access token');
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError && !options?.silentOnly) {
        const result = await instance.acquireTokenPopup({ scopes: [apiScope], account: active });
        if (!result.accessToken) throw new Error('acquireTokenPopup returned no access token');
        return result.accessToken;
      }
      throw err;
    }
  }, [instance, account, apiScope]);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      isLoading,
      error,
      account: account ? { name: account.name, username: account.username } : null,
      signIn,
      signOut,
      getAccessToken,
    }),
    [isAuthenticated, isLoading, error, account, signIn, signOut, getAccessToken]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export interface MsalAuthProviderProps {
  clientId: string;
  tenantId: string;
  /** API scope exposed in the Entra app registration (e.g. `api://<id>/access_as_user`). Used by `getAccessToken` to acquire an access token with the correct `aud` for the backend. */
  apiScope: string;
  children: ReactNode;
}

/**
 * Initialises a `PublicClientApplication` from `clientId` + `tenantId` and
 * wraps children in the MSAL React provider. Rendered by {@link AuthProvider}
 * when all three env vars are present.
 *
 * The MSAL instance is memoised — changing `clientId` or `tenantId` at runtime
 * creates a new instance, which clears cached tokens.
 */
export function MsalAuthProvider({ clientId, tenantId, apiScope, children }: MsalAuthProviderProps) {
  const msalInstance = useMemo(() => {
    const config: Configuration = {
      auth: {
        clientId,
        authority: `https://login.microsoftonline.com/${tenantId}`,
        redirectUri: window.location.origin,
      },
      cache: {
        cacheLocation: 'sessionStorage',
      },
    };
    return new PublicClientApplication(config);
  }, [clientId, tenantId]);

  return (
    <MsalProvider instance={msalInstance}>
      <MsalContextBridge apiScope={apiScope}>{children}</MsalContextBridge>
    </MsalProvider>
  );
}
